import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { ArticoloFull } from '../interfaces/custom/articolo';
import { ShopService } from '../api/shop.service';

interface WishlistData {
    items: ArticoloFull[];
}

@Injectable({
    providedIn: 'root'
})
export class WishlistService implements OnDestroy {
    private data: WishlistData = {
        items: []
    };

    private destroy$: Subject<void> = new Subject();
    private itemsSubject$: BehaviorSubject<ArticoloFull[]> = new BehaviorSubject([]);
    private onAddingSubject$: Subject<ArticoloFull> = new Subject();

    readonly items$: Observable<ArticoloFull[]> = this.itemsSubject$.pipe(takeUntil(this.destroy$));
    readonly count$: Observable<number> = this.itemsSubject$.pipe(map(items => items.length));
    readonly onAdding$: Observable<ArticoloFull> = this.onAddingSubject$.asObservable();

    constructor(
        @Inject(PLATFORM_ID)
        private platformId: any,
        private shop : ShopService
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.load();
        }
    }

    add(product: ArticoloFull): Observable<void> {
        // timer only for demo
        return timer(200).pipe(map(() => {
            this.onAddingSubject$.next(product);

            const index = this.data.items.findIndex(item => item.codart === product.codart && item.identificativo_azienda == product.identificativo_azienda);

            if (index === -1) {
                this.data.items.push(product);
                this.save();
            }
        }));
    }

    remove(product: ArticoloFull): Observable<void> {
        // timer only for demo
        return timer(200).pipe(map(() => {
            const index = this.data.items.findIndex(item => item.codart === product.codart && item.identificativo_azienda == product.identificativo_azienda);

            if (index !== -1) {
                this.data.items.splice(index, 1);
                this.save();
            }
        }));
    }

    private save(): void {
        localStorage.setItem('wishlistItems', JSON.stringify(this.data.items));

        this.itemsSubject$.next(this.data.items);
    }

    private load(): void {
        const items = localStorage.getItem('wishlistItems');

        if (items) {
            this.data.items = JSON.parse(items);
            this.itemsSubject$.next(this.data.items);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getItems(){
        let items = [];
        for (let item of this.data.items) {
            this.shop.getArticolo(item.codart).pipe(
                tap(articolo => Object.assign(item, articolo)),
                tap(articolo => items.push(articolo))
            ).subscribe();
        }
        return items;
    }

}
